import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { endSprint } from '../api/workspace';
import { queryClient } from '../queryClient';
import { getCurrentWorkspaceActiveSprint } from '../serverState/sprint';
import { TaskStatus, defaultTaskStatuses, TaskStatusGroup } from '../types/TaskStatus';
import { useGetWorkspace } from '../serverState/workspace';
import { selectSprintCountableTasks } from '../utils/taskListUtils';

export const useSprintHook = (onSuccess: (message: string) => void, onError: (message: string) => void) => {
  const workspaceQuery = useGetWorkspace();
  const activeSprintQuery = getCurrentWorkspaceActiveSprint();
  const { orgId, spaceId } = useParams();

  const navigate = useNavigate();

  const statusOptions: TaskStatus[] = (workspaceQuery.data?.TaskStatus as TaskStatus[]) || defaultTaskStatuses;
  const statusOptionsToMoveUnfinishedTasks = useMemo(() => {
    return statusOptions.filter(
      (statusOption) =>
        statusOption.group === TaskStatusGroup.inProgress || statusOption.group === TaskStatusGroup.notStarted
    );
  }, [statusOptions, workspaceQuery.data?.id]);

  // Count through the shared predicate, not the raw sprint payload: the endpoint
  // returns every row with this sprintId, epics/subitems/archived included, which
  // is why this modal reported more tasks than the sprint itself lists. #2956
  const unfinishedTasks = useMemo(() => {
    const sprintTasks = selectSprintCountableTasks(activeSprintQuery.data?.tasks, activeSprintQuery.data?.sprint?.id);
    return sprintTasks.filter((task: { statusId: number }) =>
      statusOptionsToMoveUnfinishedTasks.find((statusOption) => statusOption.id === task.statusId)
    );
  }, [activeSprintQuery.data, statusOptionsToMoveUnfinishedTasks]);

  const handleEndSprint = async (moveUnfinishedTasksToPlanning: boolean) => {
    try {
      if (activeSprintQuery.data?.sprint) {
        const response = await endSprint(activeSprintQuery.data?.sprint.id, moveUnfinishedTasksToPlanning);
        onSuccess(response.data.message);
        queryClient.invalidateQueries(['tasks']);
        queryClient.invalidateQueries(['workspace']);
        navigate(`/${orgId}/space/${spaceId}/backlog`);
      }
    } catch (error: any) {
      onError(`Failed to end sprint: ${error.message}`);
    }
  };

  return {
    unfinishedTasks,
    handleEndSprint,
  };
};
